import { create } from 'zustand';

const useUIStore = create((set, get) => ({
  isSidebarOpen: localStorage.getItem('managemate-sidebar-open') !== 'false',
  isMobileOpen: false,
  notifications: [],
  toasts: [],
  theme: localStorage.getItem('managemate-theme') || 'system', // 'light', 'dark', 'system' (color mode)
  themePreset: localStorage.getItem('managemate-theme-preset') || 'default', // 'default', 'sensei'
  density: localStorage.getItem('managemate-density') || 'comfort', // 'comfort', 'compact'

  // Global modals state
  isGlobalNoteModalOpen: false,
  isGlobalTaskModalOpen: false,
  isGlobalTicketModalOpen: false,
  globalModalProjectId: null,

  setGlobalNoteModalOpen: (open) => set({ isGlobalNoteModalOpen: open }),
  setGlobalTaskModalOpen: (open, projectId = null) =>
    set({ isGlobalTaskModalOpen: open, globalModalProjectId: projectId }),
  setGlobalTicketModalOpen: (open, projectId = null) =>
    set({ isGlobalTicketModalOpen: open, globalModalProjectId: projectId }),

  toggleSidebar: () =>
    set((state) => {
      const newOpen = !state.isSidebarOpen;
      localStorage.setItem('managemate-sidebar-open', String(newOpen));
      return { isSidebarOpen: newOpen };
    }),
  toggleMobileSidebar: () => set((state) => ({ isMobileOpen: !state.isMobileOpen })),

  // Density logic
  setDensity: (density) => {
    localStorage.setItem('managemate-density', density);
    set({ density });
    get().applyDensity(density);
  },
  applyDensity: (density) => {
    const root = window.document.documentElement;
    root.setAttribute('data-density', density);
  },

  // Theme logic
  setTheme: (theme) => {
    localStorage.setItem('managemate-theme', theme);
    set({ theme });
    get().applyTheme();
  },
  setThemePreset: (themePreset) => {
    localStorage.setItem('managemate-theme-preset', themePreset);
    set({ themePreset });
    get().applyTheme();
  },
  applyTheme: () => {
    const root = window.document.documentElement;
    const { theme, themePreset } = get();
    root.classList.remove('light', 'dark', 'sensei');

    if (themePreset === 'sensei') {
      root.classList.add('sensei');
    }

    if (theme === 'system') {
      const systemTheme = window.matchMedia('(prefers-color-scheme: dark)').matches
        ? 'dark'
        : 'light';
      root.classList.add(systemTheme);
    } else {
      root.classList.add(theme);
    }
  },
  initTheme: () => {
    get().applyTheme();
    get().applyDensity(get().density);

    // Listen for system theme changes
    window.matchMedia('(prefers-color-scheme: dark)').addEventListener('change', (e) => {
      if (get().theme === 'system') {
        get().applyTheme();
      }
    });
  },

  // Notification center logic
  setNotifications: (notifications) => set({ notifications }),
  addNotification: (notification) =>
    set((state) => ({ notifications: [notification, ...state.notifications] })),
  markAsRead: (id) =>
    set((state) => ({
      notifications: state.notifications.map((n) => (n.id === id ? { ...n, isRead: true } : n)),
    })),
  clearNotifications: () => set({ notifications: [] }),

  // Transient Toast logic
  addToast: (toast) =>
    set((state) => ({
      toasts: [...state.toasts, { ...toast, toastId: Date.now() }],
    })),
  removeToast: (toastId) =>
    set((state) => ({
      toasts: state.toasts.filter((t) => t.toastId !== toastId),
    })),
}));

export default useUIStore;
